import { type FC, lazy, Suspense, useCallback, useLayoutEffect, useMemo, useState } from "react"

import type { LandingPageLinkOption } from "@forento/core/models/landing"
import { landingPageCopyCharacterLimit } from "@forento/core/schemas/landing"
import {
	getPlainOrLexicalTextCharacterCount,
	isLexicalJson,
	plainTextToLexicalJson,
} from "@forento/core/utilities/lexical/index"
import { type LexicalEditorType, type LexicalLinkOptionGroup } from "@forento/ui/components/LexicalEditor"
import { cn } from "@forento/ui/utilities/tailwind"

import { query } from "~/utilities/trpc"

import CharacterCounter from "./CharacterCounter"

const LexicalEditor = lazy(() => import("@forento/ui/components/LexicalEditor"))

type Props = {
	initialValue: string
	onChange: (lexicalJson: string | null) => void
	className?: string
	contentClassName?: string
	maxCharacters?: number
}
const RichEditableField: FC<Props> = ({
	initialValue,
	onChange,
	className,
	contentClassName,
	maxCharacters = landingPageCopyCharacterLimit,
}) => {
	const { data: linkOptions } = query.landing.linkOptions.useQuery(undefined, { refetchOnWindowFocus: false })
	const [editor, setEditor] = useState<LexicalEditorType | null>(null)
	const [characterCount, setCharacterCount] = useState(() => getPlainOrLexicalTextCharacterCount(initialValue))
	const [initialEditorState] = useState(() => {
		if (isLexicalJson(initialValue)) return initialValue
		if (initialValue) return plainTextToLexicalJson(initialValue)
		return null
	})

	useLayoutEffect(() => {
		if (!editor) return
		return editor.registerUpdateListener(({ editorState, dirtyElements, dirtyLeaves }) => {
			if (dirtyElements.size === 0 && dirtyLeaves.size === 0) return
			const json = JSON.stringify(editorState.toJSON())
			setCharacterCount(getPlainOrLexicalTextCharacterCount(json))
			onChange(json)
		})
	}, [editor, onChange])

	const handleEditorRef = useCallback((e: LexicalEditorType) => {
		setEditor(e)
	}, [])

	// An inline link is a plain href inside the Lexical document rather than a typed link target, so an
	// academy page has to be offered as an absolute url here.
	const linkOptionGroups = useMemo<LexicalLinkOptionGroup[]>(() => {
		if (linkOptions === undefined) return []

		const toGroup = (label: string, options: LandingPageLinkOption[], prefix: string) => ({
			label,
			options: options.map(option => ({ url: `${prefix}${option.path}`, label: option.label })),
		})

		return [
			...(linkOptions.sitePages.length > 0 ? [toGroup("Your website", linkOptions.sitePages, "")] : []),
			...linkOptions.academyPages.map(group => toGroup(group.label, group.options, linkOptions.academyUrl)),
		]
	}, [linkOptions])

	return (
		<div
			className={cn(
				"min-h-20 rounded-md border border-stone-200 bg-white p-3 transition-shadow duration-150 focus-within:ring-2 focus-within:ring-stone-300/70",
				className,
			)}
		>
			<Suspense>
				<LexicalEditor
					key={initialEditorState ?? "empty"}
					editorRef={handleEditorRef}
					initialEditorState={initialEditorState}
					maxCharacters={maxCharacters}
					contentClassName={contentClassName}
					linkOptionGroups={linkOptionGroups}
				/>
			</Suspense>
			<CharacterCounter count={characterCount} limit={maxCharacters} />
		</div>
	)
}

export default RichEditableField
